import React, { useEffect, useState } from 'react';
import {
  IonBackButton,
  IonBadge,
  IonButtons,
  IonCard,
  IonCardContent,
  IonToggle,
  IonContent,
  IonHeader,
  IonItem,
  IonItemDivider,
  IonItemGroup,
  IonLabel,
  IonModal,
  IonPage,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { PDFDocument, PDFFont, PDFPage, StandardFonts } from 'pdf-lib';
import { useHistory } from 'react-router-dom';
import { ApiService } from '../api/ApiService';
import { DriverProfile } from '../api/model/DriverProfile';
import { toastStore } from '../components/AppToast';
import {
  FIELD_PDF_LAYOUT,
  FormField,
  FORM_IMAGE,
  HEADER_PDF_LAYOUT,
  INSPECTION_FIELDS,
  PDF_SIZE,
  SECTION_LABELS,
  SECTION_ORDER,
  getCheckboxCount,
  getFieldsBySection,
  imageToPdf,
} from './returnedUnitFormFields';

type CheckboxState = Record<string, boolean[]>;
type CommentState = Record<string, string>;

function buildInitialCheckboxState(): CheckboxState {
  const state: CheckboxState = {};
  for (const section of SECTION_ORDER) {
    for (const field of getFieldsBySection(section)) {
      state[field.id] = Array(getCheckboxCount(field)).fill(false);
    }
  }
  return state;
}

function buildInitialCommentState(): CommentState {
  const state: CommentState = {};
  for (const section of SECTION_ORDER) {
    for (const field of getFieldsBySection(section)) {
      state[field.id] = '';
    }
  }
  return state;
}

const b64toBlob = (base64: string) => fetch(base64).then((res) => res.blob());

function formatDisplayDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) {
    return isoDate;
  }
  return `${month}/${day}/${year}`;
}

function truncateComment(text: string, maxLength = 80): string {
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLength - 3)}...`;
}

function drawCheckbox(page: PDFPage, x: number, y: number, font: PDFFont) {
  page.drawText('X', { x: x - 2, y: y - 4, size: 9, font });
}

function drawComment(
  page: PDFPage,
  x: number,
  y: number,
  maxWidth: number,
  text: string,
  font: PDFFont,
) {
  const value = truncateComment(text);
  if (!value) {
    return;
  }
  page.drawText(value, { x, y, size: 7, font, maxWidth });
}

async function buildReturnedUnitPdf(
  unit: string,
  driverCode: string,
  driverName: string,
  date: string,
  checkboxes: CheckboxState,
  comments: CommentState,
): Promise<string> {
  const formURL = './assets/Returned Unit Form.pdf';
  const pdfFormBytes = await fetch(formURL).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(pdfFormBytes);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const page = pdfDoc.getPages()[0];

  const headerValues = { unit, driverCode, driverName, date: formatDisplayDate(date) };
  for (const [key, point] of Object.entries(HEADER_PDF_LAYOUT)) {
    const { x, y } = imageToPdf(point);
    page.drawText(headerValues[key as keyof typeof headerValues], { x, y, size: 9, font });
  }

  for (const field of INSPECTION_FIELDS) {
    const layout = FIELD_PDF_LAYOUT[field.id];
    if (!layout) {
      continue;
    }

    const fieldChecks = checkboxes[field.id] ?? [];
    layout.checkboxes.forEach((point, index) => {
      if (fieldChecks[index]) {
        const { x, y } = imageToPdf(point);
        drawCheckbox(page, x, y, font);
      }
    });

    const comment = comments[field.id]?.trim();
    if (comment) {
      const { x, y, maxWidth } = layout.comment;
      const pdfPoint = imageToPdf({ x, y });
      drawComment(page, pdfPoint.x, pdfPoint.y, (maxWidth / FORM_IMAGE.width) * PDF_SIZE.width, comment, font);
    }
  }

  return pdfDoc.saveAsBase64({ dataUri: true });
}

const ReturnedUnitForm: React.FC = () => {
  const history = useHistory();
  const today = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .split('T')[0];

  const [unit, setUnit] = useState('');
  const [estimator, setEstimator] = useState('');
  const [date, setDate] = useState(today);
  const [checkboxes, setCheckboxes] = useState<CheckboxState>(buildInitialCheckboxState);
  const [comments, setComments] = useState<CommentState>(buildInitialCommentState);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    ApiService.getDriverProfile('returnedUnitForm').then((profile: DriverProfile) => {
      if (profile.unit) {
        setUnit(profile.unit);
      }
    });
  }, []);

  const isFieldChecked = (field: FormField): boolean => {
    if (!field.hasCheckbox) {
      return true;
    }
    return (checkboxes[field.id] ?? []).some(Boolean);
  };

  const setCheckbox = (fieldId: string, index: number, checked: boolean) => {
    setCheckboxes((prev) => {
      const next = [...prev[fieldId]];
      next[index] = checked;
      const updated = { ...prev, [fieldId]: next };
      if (!next.some(Boolean)) {
        setComments((commentsPrev) => ({ ...commentsPrev, [fieldId]: '' }));
      }
      return updated;
    });
  };

  const setComment = (fieldId: string, value: string) => {
    setComments((prev) => ({ ...prev, [fieldId]: value }));
  };

  const hasInspectionInput = (): boolean => { 
    for (const section of SECTION_ORDER) {
      for (const field of getFieldsBySection(section)) {
        if (checkboxes[field.id]?.some(Boolean)) {
          return true;
        }
        if (comments[field.id]?.trim()) {
          return true;
        }
      }
    }
    return false;
  };

  const validateForm = (): string | null => {
    if (!unit.trim()) {
      return 'Please enter a unit number.';
    }
    if (!estimator.trim()) {
      return 'Please enter an estimator name.';
    } 
    if (!date) {
      return 'Please enter a date.';
    }
    if (!hasInspectionInput()) {
      return 'Please check at least one inspection item or add a comment.';
    }
    return null;
  };

  const showValidationError = (message: string) => {
    setErrorMessage(message);
    setShowError(true);
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      showValidationError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const pdfData = await buildReturnedUnitPdf(
        unit.trim(),
        estimator.trim(),
        '',
        date,
        checkboxes,
        comments,
      );
      const tempFile = new Blob([await b64toBlob(pdfData)], { type: 'application/pdf' });

      await ApiService.sendFileEmail(
        'returnedUnitForm',
        {
          email: 'lpdocuments@westernexp.com, scarney@westernexp.com',
          subject: `Returned Unit Form - Unit ${unit.trim()} - ${estimator.trim()}`,
        },
        {
          file1: {
            fileName: 'Returned Unit Form.pdf',
            file: tempFile,
          },
        },
      );

      await ApiService.sendEventCommand(
        'returnedUnitForm',
        'eventCheck',
        'User submitted a returned unit form',
      );
      toastStore.getState().setSuccessToast('Success. Your form was submitted.');
      history.push('/docs');
    } catch (error) {
      console.error('Returned unit form submission failed', error);
      showValidationError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderInspectionRow = (field: FormField) => {
    const checkboxCount = getCheckboxCount(field);
    const commentsEnabled = isFieldChecked(field);

    return (
      <div
        key={field.id}
        style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          marginBottom: 10,
          padding: '12px 14px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: commentsEnabled ? 10 : 4 }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: '#0E375A', flex: 1 }}>{field.label}</span>
          {commentsEnabled && (
            <IonBadge color="success" style={{ fontSize: 10, marginLeft: 8 }}>Flagged</IonBadge>
          )}
        </div>

        {field.hasCheckbox && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
            {Array.from({ length: checkboxCount }, (_, index) => (
              <div
                key={`${field.id}-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 0',
                  borderBottom: index < checkboxCount - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                <span style={{ fontSize: 14, color: '#444' }}>
                  {checkboxCount > 1 ? `Item ${index + 1}` : 'Flag for repair'}
                </span>
                <IonToggle
                  checked={checkboxes[field.id][index]}
                  onIonChange={(e) => setCheckbox(field.id, index, e.detail.checked)}
                  style={{ '--track-background-checked': '#ED8254', '--handle-background': '#fff' } as React.CSSProperties}
                />
              </div>
            ))}
          </div>
        )}

        <IonTextarea
          value={comments[field.id]}
          onIonInput={(e) => setComment(field.id, e.detail.value ?? '')}
          placeholder={commentsEnabled ? 'Add repair notes...' : 'Flag an item above to add notes'}
          rows={commentsEnabled ? 2 : 1}
          autoGrow={commentsEnabled}
          disabled={!commentsEnabled}
          style={{
            '--background': commentsEnabled ? '#f8f9fa' : '#f0f0f0',
            '--border-radius': '8px',
            '--padding-start': '10px',
            '--padding-end': '10px',
            '--padding-top': '8px',
            '--padding-bottom': '8px',
            fontSize: 13,
            color: commentsEnabled ? '#212121' : '#aaa',
            borderRadius: 8,
            border: commentsEnabled ? '1px solid #e0e0e0' : '1px solid #e8e8e8',
            transition: 'all 0.2s',
          } as React.CSSProperties}
        />
      </div>
    );
  };

  const fieldStyle: React.CSSProperties = {
    boxSizing: 'border-box',
    width: '100%',
    border: 'none',
    outline: 'none',
    borderRadius: 10,
    minHeight: 48,
    backgroundColor: '#fff',
    fontSize: 16,
    padding: '0 14px',
    color: '#212121',
  };

  const fieldLabelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    marginBottom: 4,
    display: 'block',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  return (
    <IonPage>
      <IonToast
        isOpen={showError}
        onDidDismiss={() => setShowError(false)}
        message={errorMessage}
        color="danger"
        duration={2500}
      />
      <IonModal isOpen={submitting} id="transparent">
        <div className="modal-div">
          <div id="modal">
            <div className="loading-div">
              <p>Please wait... your form is being submitted.</p>
              <div className="snippet" data-title=".dot-flashing" style={{ margin: '4px 16px' }}>
                <div className="stage">
                  <div className="dot-flashing" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </IonModal>

      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonBackButton defaultHref="/docs" text="" />
          </IonButtons>
          <IonTitle>Returned Unit Form</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color="light">
        <div style={{ padding: '16px 14px 32px' }}>

          {/* Page intro */}
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ margin: '0 0 4px', color: '#0E375A', fontSize: 20, fontWeight: 700 }}>Repairs Needed for Unit</h2>
            <p style={{ margin: 0, color: '#666', fontSize: 14 }}>Flag each item that requires repair and add notes as needed.</p>
          </div>

          {/* Unit information card */}
          <IonCard style={{ borderRadius: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', margin: '0 0 20px', overflow: 'hidden' }}>
            <div style={{ background: '#0E375A', padding: '10px 16px' }}>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Unit Information</span>
            </div>
            <IonCardContent style={{ padding: '16px', background: '#0E375A' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label htmlFor="unit" style={fieldLabelStyle}>Unit #</label>
                  <input
                    id="unit"
                    type="text"
                    placeholder="e.g. 12345"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    style={fieldStyle}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label htmlFor="estimator" style={fieldLabelStyle}>Estimator</label>
                    <input
                      id="estimator"
                      type="text"
                      placeholder="Estimator name"
                      value={estimator}
                      onChange={(e) => setEstimator(e.target.value)}
                      style={fieldStyle}
                    />
                  </div>
                  <div>
                    <label htmlFor="date" style={fieldLabelStyle}>Date</label>
                    <input
                      id="date"
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      style={fieldStyle}
                    />
                  </div>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Inspection sections */}
          {SECTION_ORDER.map((section) => {
            const fields = getFieldsBySection(section);
            const flaggedCount = fields.filter(isFieldChecked).length;
            return (
              <div key={section} style={{ marginBottom: 20 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#0E375A',
                  borderRadius: '10px 10px 0 0',
                  padding: '8px 14px',
                  marginBottom: 2,
                }}>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                    {SECTION_LABELS[section]}
                  </span>
                  {flaggedCount > 0 && (
                    <IonBadge style={{ backgroundColor: '#ED8254', color: '#fff', fontSize: 11 }}>
                      {flaggedCount} flagged
                    </IonBadge>
                  )}
                </div>
                <div style={{ backgroundColor: '#f2f4f7', borderRadius: '0 0 10px 10px', padding: '10px 10px 2px' }}>
                  {fields.map(renderInspectionRow)}
                </div>
              </div>
            );
          })}

          {/* Submit */}
          <button
            className="western-button submit"
            disabled={submitting}
            onClick={handleSubmit}
            style={{ width: '100%', marginTop: 8, padding: '14px', fontSize: 16 }}
          >
            <div id="btn-text">Submit Form</div>
          </button>

        </div>
      </IonContent>
    </IonPage>
  );
};

export default ReturnedUnitForm;
